import { DukeTiptapEditor } from '@duke-hq/ui/components/DukeTiptapEditor/DukeTiptapEditor';
import { RecordResponse } from '@duke-hq/ui/components/MediaRecorder/RecordResponse';
import { useFlag } from '@duke-hq/flags/react';
import { Trans } from '@lingui/react/macro';
import { SlideKind } from '@duke-hq/svc-api-db';
import { UnitAssessmentSlides } from '../types';

import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';

import { Upload } from 'lucide-react';

type Props = {
  slide: Extract<UnitAssessmentSlides[0], { kind: typeof SlideKind.FILE_UPLOAD_PROMPT }>;
};

function FilePrompt({ slide }: Props) {
  const isMediaRecordingReleased = useFlag('ASSESSMENT_MEDIA_RECORDING_RELEASE') === true;
  const recordingKinds = isMediaRecordingReleased ? slide.recordingKinds : [];

  return (
    <Flex flexDir="column" alignItems="center">
      <Box w="full" h="full" p="8px">
        <Text fontSize="xl" fontWeight="semibold" color="fg.default" mb={4}>
          <Trans>Task</Trans>
        </Text>
        <DukeTiptapEditor initialContent={slide.questionContent} readOnly onChange={() => {}} />
      </Box>
      <Box w="full" h="full" p="8px">
        <Text fontSize="md" fontWeight="semibold" color="fg.default" mb={4}>
          {recordingKinds.length > 0 ? (
            <Trans>Record or upload your response</Trans>
          ) : (
            <Trans>File Upload</Trans>
          )}
        </Text>
        {recordingKinds.length > 0 ? (
          <Box mb={4}>
            <RecordResponse recordingKinds={recordingKinds} isDisabled />
          </Box>
        ) : null}
        <Center
          borderWidth="2px"
          borderRadius="lg"
          borderStyle="dotted"
          px="6"
          py="4"
          borderColor="border.accent"
          position="relative"
        >
          <input
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
            }}
          />

          <VStack spacing="3">
            <IconButton
              aria-label={'Upload'}
              icon={<Upload size="24" color="currentColor" />}
              variant="ghost"
            />
            <VStack spacing="1">
              <HStack spacing="1" whiteSpace="nowrap">
                <Button variant="text" color="fg.muted" size="md">
                  Click to upload
                </Button>
                <Text textStyle="sm" color="fg.muted">
                  or drag and drop
                </Text>
              </HStack>
              <Text textStyle="xs" color="fg.muted">
                PDF, Word, Excel, PowerPoint, image or video, up to 1GB each
              </Text>
            </VStack>
          </VStack>
        </Center>
      </Box>
      <Box w="full" h="full" p="8px">
        <Text fontSize="md" color="fg.default" mb={4}>
          <Trans>Supporting text</Trans>
        </Text>
        <Textarea disabled />
      </Box>
      <Box w="full" h="full" bg="bg.subtle" p="8px" borderRadius="8px">
        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
          <Trans>Hint</Trans>
        </Text>
        <DukeTiptapEditor initialContent={slide.hint} readOnly onChange={() => {}} />
      </Box>
    </Flex>
  );
}

export default FilePrompt;
